import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { BadgeSuccess, LinearProgressFlujo, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetConditionsByIdQuery, useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import useInterval from "@hooks/useInterval";
import { formatCurrency, formatPercent } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

const SECONDS_TO_FETCH = 10;

const PASOS_COMPLETADOS = [
  { label: "Solicitud", description: "Información completada" },
  { label: "Empresa", description: "Documentos validados" },
  { label: "Personas relacionadas", description: "Información completa" },
];

interface DatoLineaProps {
  label: string;
  value: string;
}

const DatoLinea = ({ label, value }: DatoLineaProps) => (
  <Box>
    <Typography
      variant="body2"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.06em"
      color="tokens.textTertiary"
      mb="3px"
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={600}
      fontFamily="Outfit"
      sx={{ fontVariantNumeric: "tabular-nums" }}
      color="primary.dark"
    >
      {value}
    </Typography>
  </Box>
);

interface StepRowProps {
  label: string;
  description: string;
}

/**
 * `StepRow` (estado `done`) del diseño: círculo verde de 22px con check, etiqueta con su descripción y la
 * pastilla "Completado" a la derecha.
 */
const StepRow = ({ label, description }: StepRowProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "success.main",
        color: "#fff",
      }}
    >
      <Check sx={{ fontSize: 13 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 500, color: "primary.dark" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "tokens.textTertiary" }}>
        {description}
      </Typography>
    </Box>
    <BadgeSuccess>Completado</BadgeSuccess>
  </Box>
);

/**
 * Expediente enviado a revisión (`S_ExpReview` del diseño): avance de las secciones del expediente y el
 * resumen de la línea preaprobada.
 */
export const RevisionExpedienteFactoraje = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const application = useSelector((state: RootState) => state.register.application);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);

  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();

  const [conditions, setConditions] = useState<LoanCondiciones | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadConditions = useCallback(async () => {
    if (!application?.id) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await getConditions(application.id).unwrap();
      setConditions(data[0] ?? null);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "RevisionExpedienteFactoraje" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [application?.id, getConditions, dispatch]);

  const statusPoll = useInterval(async () => {
    try {
      const loanApplication = await getLoanApplication(application.id).unwrap();
      if (loanApplication.current_status?.code && loanApplication.current_status.code !== loanStatus) {
        statusPoll.setIsActiveInterval(false);
        navigate(`/${loanApplication.current_status.url}`);
      }
    } catch (error) {
      console.error("Error al consultar el estatus de la solicitud", error);
    }
  }, SECONDS_TO_FETCH);

  const { setIsActiveInterval } = statusPoll;

  useEffect(() => {
    loadConditions();
  }, [loadConditions]);

  useEffect(() => {
    setIsActiveInterval(true);
    return () => setIsActiveInterval(false);
  }, [setIsActiveInterval]);

  const datosLinea: DatoLineaProps[] = [
    { label: "Línea preaprobada", value: formatCurrency(conditions?.approved_amount ?? 0) },
    { label: "Aforo", value: formatPercent(conditions?.extra_conditions?.factoring?.advance_rate ?? 0) },
    { label: "Tasa anual", value: formatPercent(conditions?.interest_rate ?? 0) },
    {
      label: "Comisión por operación",
      value: formatPercent(conditions?.extra_conditions?.factoring?.operation_fee ?? 0),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Estado"
        title="Tu expediente está en revisión"
        subtitle="Estamos validando los documentos y la información que enviaste. Te avisaremos por correo y WhatsApp en cuanto haya novedades.  Una vez revisado tu expediente, un ejecutivo estará en contacto para agendar la visita ocular."
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <Paper variant="lendia_paper">
            <LinearProgressFlujo value={100} data-testid="expediente-revision-progress" />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {PASOS_COMPLETADOS.map((paso) => (
                <StepRow key={paso.label} label={paso.label} description={paso.description} />
              ))}
            </Box>
          </Paper>

          <Paper variant="lendia_paper" sx={{ mt: "18px", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: "12px" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "primary.main" }}
              >
                Resumen de tu línea
              </Typography>
              <BadgeSuccess>Preaprobada</BadgeSuccess>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(max-content, 1fr))" },
                gap: "14px 24px",
              }}
            >
              {datosLinea.map(({ label, value }) => (
                <DatoLinea key={label} label={label} value={value} />
              ))}
            </Box>

            {conditions?.approved_max_term && (
              <Typography variant="body2" component="p" sx={{ color: "tokens.textTertiary", mt: "17px" }}>
                Plazo máximo de factura <strong>{conditions?.approved_max_term} días</strong> · la línea se libera
                conforme te pagan tus clientes.
              </Typography>
            )}
          </Paper>

          <StepActions
            onBack={() => undefined}
            onNext={() => navigate(PATHS_EXPEDIENTE_V2.expedienteAutorizado.path)}
            nextLabel="Ver resultado final"
            backTestId="expediente-revision-back-button"
            nextTestId="expediente-revision-continue-button"
          />
        </>
      )}
    </Box>
  );
};
